import { ChatMessages } from "@storytell/cp-client";
import { dispatchGlobalEvent } from "@storytell/events";
import { richTextToProseMirror } from "@storytell/rich-text/src/converters/richTextToProseMirror";
import { produce } from "solid-js/store";
import { emitBlockDelta, emitBlockStop, getBlockContent, updateOperationDeltaTimestamp } from "../../granularStores";
import { accumulateAggregatedMetrics } from "../../types";
import type {
  SetStreamingStore,
  StreamingBlock,
  StreamingBlockId,
  StreamingBlockMetrics,
  StreamingChatId,
  StreamingMessageId,
  StreamingOperation,
  StreamingOperationId,
  StreamingStore,
  StreamingTask,
} from "../streamingTypes";
import { attachOperationToMessage, recordOrphanedBlock } from "./streamingOperationHandlers";

// A delta's metrics count only its own chunk, so the deltas a block receives within a frame are
// summed here and the flush adds that sum to the block, message and chat totals once per frame.
type PendingMetrics = StreamingBlockMetrics & {
  messageId: StreamingMessageId;
  chatId: StreamingChatId | undefined;
  totalCredits: number;
};

const pendingMetrics = new Map<StreamingBlockId, PendingMetrics>();
let metricsRafId: number | undefined;
let currentSetStore: SetStreamingStore | null = null;

const addMetrics = (into: StreamingBlockMetrics, from: StreamingBlockMetrics) => {
  into.ingressTokens += from.ingressTokens;
  into.egressTokens += from.egressTokens;
  into.ingressBytes += from.ingressBytes;
  into.egressBytes += from.egressBytes;
  into.ingressCredits = (into.ingressCredits ?? 0) + (from.ingressCredits ?? 0);
  into.egressCredits = (into.egressCredits ?? 0) + (from.egressCredits ?? 0);
};

const flushPendingMetrics = () => {
  metricsRafId = undefined;

  if (pendingMetrics.size === 0 || !currentSetStore) return;

  // Totals are written field by field rather than replaced: a store write of an equal value is a
  // no-op, so a reader of one field (a turn's credits) only re-runs when that field moves.
  currentSetStore(
    produce((store: StreamingStore) => {
      for (const [blockId, pending] of pendingMetrics) {
        const block = store.blocks[blockId];
        if (block?.type === "block") {
          if (block.metrics) {
            addMetrics(block.metrics, pending);
          } else {
            const { ingressTokens, egressTokens, ingressBytes, egressBytes, ingressCredits, egressCredits } = pending;
            block.metrics = { ingressTokens, egressTokens, ingressBytes, egressBytes, ingressCredits, egressCredits };
          }
        }

        const message = store.messages[pending.messageId];
        if (!message) continue;
        const ingressCredits = pending.ingressCredits ?? 0;
        const egressCredits = pending.egressCredits ?? 0;
        const featureTokens =
          ingressCredits > 0 || egressCredits > 0 || pending.totalCredits > 0
            ? { ingressCredits, egressCredits, totalCredits: pending.totalCredits }
            : undefined;
        const chat = pending.chatId ? store.chats[pending.chatId] : undefined;
        for (const target of chat ? [message, chat] : [message]) {
          Object.assign(
            target.aggregateMetrics,
            accumulateAggregatedMetrics(
              target.aggregateMetrics,
              pending.ingressTokens,
              pending.egressTokens,
              pending.ingressBytes,
              pending.egressBytes,
              featureTokens
            )
          );
        }
      }
    })
  );

  pendingMetrics.clear();
};

const scheduleMetricsFlush = (setStore: SetStreamingStore) => {
  currentSetStore = setStore;
  if (metricsRafId !== undefined) return;
  metricsRafId = requestAnimationFrame(flushPendingMetrics);
};

const getBlockMetrics = (message: ChatMessages.Message): StreamingBlockMetrics | undefined => {
  if (
    message.ingressTokens > 0 ||
    message.egressTokens > 0 ||
    message.ingressBytes > 0 ||
    message.egressBytes > 0 ||
    message.ingressCredits > 0 ||
    message.egressCredits > 0
  ) {
    return {
      ingressTokens: Number(message.ingressTokens),
      egressTokens: Number(message.egressTokens),
      ingressBytes: Number(message.ingressBytes),
      egressBytes: Number(message.egressBytes),
      ingressCredits: Number(message.ingressCredits),
      egressCredits: Number(message.egressCredits),
    };
  }
  return undefined;
};

export const processBlockStart = (
  setStore: SetStreamingStore,
  message: ChatMessages.Message,
  eventTimestamp?: Date
) => {
  if (message.data.case === "messageTaskData") return processTaskStart(setStore, message, eventTimestamp);

  if (message.data.case !== "messageStartData") {
    console.error(new Error(`Expected messageStartData for block start, got ${message.data.case}`));
    return;
  }

  const {
    id,
    role,
    blockId: _blockId,
    operationId: _operationId,
    activity,
    data: { value: data },
  } = message;

  const blockId = _blockId as StreamingBlockId;
  const operationId = _operationId as StreamingOperationId;
  const messageId = id as StreamingMessageId;

  const streamingBlock: StreamingBlock = {
    isComplete: false,
    type: "block",
    blockId,
    operationId,
    messageId,
    role,
    activity,
    feature: data.feature,
    messageType: data.messageType,
    title: data.title,
    finalContent: undefined,
    searchResults: undefined,
    webSearchResults: undefined,
    conceptResults: undefined,
    structuredResults: undefined,
    imageResults: undefined,
    metrics: getBlockMetrics(message),
  };

  setStore(
    produce((store) => {
      const operation = store.operations[operationId];
      if (!operation) {
        recordOrphanedBlock(operationId, blockId);
      } else if (!operation.blocks.includes(blockId)) {
        operation.blocks.push(blockId);
      }
      store.blocks[blockId] = streamingBlock;

      const buffered = store.bufferedDeltas[blockId];
      if (buffered && buffered.length > 0) {
        for (const bufferedMsg of buffered) {
          if (bufferedMsg.data.case === "messageDeltaData") {
            const deltaText = bufferedMsg.data.value?.text;
            if (deltaText) {
              emitBlockDelta(blockId, deltaText, operationId);
            }
          }
        }
        delete store.bufferedDeltas[blockId];
      }
    })
  );
};

// Runs once per delta, so the store is only read here; the one write is the rare delta that
// arrives before its block has started.
export const processBlockDelta = (
  store: StreamingStore,
  setStore: SetStreamingStore,
  message: ChatMessages.Message
) => {
  if (message.data.case === "messageTaskData") return processTaskDelta(setStore, message);

  if (message.data.case !== "messageDeltaData" && message.data.case !== "messageData") {
    console.error(new Error(`Expected messageDeltaData, got ${message.data.case}`));
    return;
  }

  const { blockId: _blockId, operationId: _operationId } = message;
  const blockId = _blockId as StreamingBlockId;
  const operationId = _operationId as StreamingOperationId;

  const block = store.blocks[blockId];
  if (!block) {
    console.warn(`Delta for block ${blockId} arrived before start, buffering`);
    setStore(
      produce((draft) => {
        (draft.bufferedDeltas[blockId] ??= []).push(message);
      })
    );
    return;
  }

  if (block.type !== "block") {
    console.error(new Error(`Block is a task, not a block: ${blockId}`));
    return;
  }

  if (message.data.case === "messageDeltaData") {
    const deltaText = message.data.value?.text;
    if (deltaText) {
      emitBlockDelta(blockId, deltaText, operationId);
    } else {
      updateOperationDeltaTimestamp(operationId);
    }
  }

  const metrics = getBlockMetrics(message);
  if (metrics) {
    const pending = pendingMetrics.get(blockId);
    const totalCredits = Number(message.creditsTotal || 0);
    if (pending) {
      addMetrics(pending, metrics);
      pending.totalCredits += totalCredits;
    } else {
      pendingMetrics.set(blockId, {
        ...metrics,
        messageId: block.messageId,
        chatId: store.messages[block.messageId]?.chatId,
        totalCredits,
      });
    }
    scheduleMetricsFlush(setStore);
  }
};

// Structured results arrive as the protobuf message or as raw JSON, which keeps the snake_case names.
type StructuredResultsWire = Partial<ChatMessages.StructuredDataResult> & {
  asset_name?: string;
  row_count?: number;
  is_completion?: boolean;
  asset_count?: number;
  query_reason?: string;
};

export const processBlockStop = (setStore: SetStreamingStore, message: ChatMessages.Message, eventTimestamp?: Date) => {
  if (message.data.case === "messageTaskData") return processTaskDelta(setStore, message, eventTimestamp);

  if (message.data.case !== "messageData" && message.data.case !== undefined) {
    console.error(new Error(`Expected messageData or undefined, got ${message.data.case}`));
    return;
  }

  const { blockId: _blockId } = message;
  const blockId = _blockId as StreamingBlockId;

  // The block's last frame of delta metrics lands before the stop, whose credit statement
  // replaces the credit totals rather than adding to them.
  flushPendingMetrics();

  setStore(
    produce((store) => {
      const block = store.blocks[blockId];
      if (!block) {
        // Don't delete bufferedDeltas - BLOCK_START may arrive later in corrupted streams
        console.warn(`Block ${blockId} not found for stop, skipping`);
        return;
      }
      if (block.type !== "block") {
        console.error(new Error(`Block is a task, not a block: ${blockId}`));
        return;
      }

      block.isComplete = true;
      if (message.data.case === "messageData") {
        const data = message.data.value;

        if (data.content.case === "text") {
          block.finalContent = { type: "text", text: data.content.value };
        } else if (data.content.case === "richText") {
          block.finalContent = {
            type: "richText",
            richText: richTextToProseMirror(data.content.value, { autolinkBareUrls: true }),
          };
        }

        const linkedMessage = store.messages[block.messageId];
        const chat = linkedMessage ? store.chats[linkedMessage.chatId] : undefined;

        if (data.searchResults) {
          block.searchResults = data.searchResults;
          if (chat && data.searchResults.toolId) {
            // One tool call can run several searches that share a toolId, so key by block.
            chat.kbSearchRegistry.set(blockId, data.searchResults);
          }
        }

        if (data.webSearchResults) {
          block.webSearchResults = data.webSearchResults;
          if (chat && data.webSearchResults.toolId) {
            chat.webSearchRegistry.set(data.webSearchResults.toolId, data.webSearchResults);
          }
        }

        if (data.imageResults) {
          block.imageResults = data.imageResults;
        }

        if (data.conceptResults) {
          block.conceptResults = data.conceptResults;
        }

        // Backend sends raw data with headers and rows arrays
        // Support both camelCase (protobuf) and snake_case (raw JSON) field names
        if (data.structuredResults) {
          const pbResult: StructuredResultsWire = data.structuredResults;
          block.structuredResults = {
            assetName: pbResult.assetName || pbResult.asset_name || "",
            headers: pbResult.headers,
            rows: pbResult.rows?.map((row: { values?: string[] }) => row.values || []),
            rowCount: pbResult.rowCount ?? pbResult.row_count,
            isCompletion: pbResult.isCompletion ?? pbResult.is_completion,
            duration: pbResult.duration ? Number(pbResult.duration.seconds) : undefined,
            assetCount: pbResult.assetCount ?? pbResult.asset_count,
            queryReason: pbResult.queryReason || pbResult.query_reason,
            query: pbResult.query,
          };
        } else if (block.title?.includes("Structured Data") || block.activity?.includes("Structured Data")) {
          const assetName =
            block.title?.replace("Structured Data Query Results", "").trim() ||
            block.activity?.replace("Structured Data Query Results", "").trim() ||
            "Unknown Asset";

          const markdownTable = data.content.case === "text" ? data.content.value : undefined;

          block.structuredResults = {
            assetName,
            markdownTable,
          };
        }

        if (data.creditStatement) {
          const creditStatement = data.creditStatement;

          let totalFinalCredits = 0;
          let totalFinalIngressCredits = 0;
          let totalFinalEgressCredits = 0;
          let totalFinalIngressTokens = 0;
          let totalFinalEgressTokens = 0;

          if (creditStatement.debits) {
            for (const debit of creditStatement.debits) {
              if (debit.metrics?.metrics) {
                totalFinalIngressTokens += Number(debit.metrics.metrics.ingressTokens) || 0;
                totalFinalEgressTokens += Number(debit.metrics.metrics.egressTokens) || 0;
              }
              if (
                debit.metrics?.featureTokens &&
                debit.metrics.featureTokens.type === ChatMessages.FeatureTokenType.FINAL
              ) {
                totalFinalCredits += Number(debit.metrics.featureTokens.totalCredits || 0);
                totalFinalIngressCredits += Number(debit.metrics.featureTokens.ingressCredits || 0);
                totalFinalEgressCredits += Number(debit.metrics.featureTokens.egressCredits || 0);
              }
            }
          }

          if (linkedMessage) {
            if (chat) {
              chat.aggregateMetrics = {
                ...chat.aggregateMetrics,
                totalCredits: totalFinalCredits,
                ingressCredits: totalFinalIngressCredits,
                egressCredits: totalFinalEgressCredits,
                ingressTokens: totalFinalIngressTokens,
                egressTokens: totalFinalEgressTokens,
              };
            }
            linkedMessage.aggregateMetrics = {
              ...linkedMessage.aggregateMetrics,
              totalCredits: totalFinalCredits,
              ingressCredits: totalFinalIngressCredits,
              egressCredits: totalFinalEgressCredits,
              ingressTokens: totalFinalIngressTokens,
              egressTokens: totalFinalEgressTokens,
            };
          }
        }

        let totalFeatureCredits = 0;
        let totalIngressCredits = 0;
        let totalEgressCredits = 0;
        let isFinalTokenType = false;
        if (data.metrics) {
          for (const metric of data.metrics) {
            if (metric.featureTokens) {
              // Convert BigInt to Number for credits (safe for credit values)
              totalFeatureCredits += Number(metric.featureTokens.totalCredits || 0);
              totalIngressCredits += Number(metric.featureTokens.ingressCredits || 0);
              totalEgressCredits += Number(metric.featureTokens.egressCredits || 0);
              if (metric.featureTokens.type === ChatMessages.FeatureTokenType.FINAL) {
                isFinalTokenType = true;
              }
            }
          }
          // Only update if no creditStatement was processed (avoid double processing)
          if (totalFeatureCredits > 0 && !data.creditStatement) {
            if (linkedMessage) {
              if (chat) {
                chat.aggregateMetrics = {
                  ...chat.aggregateMetrics,
                  totalCredits: isFinalTokenType
                    ? totalFeatureCredits
                    : (chat.aggregateMetrics.totalCredits || 0) + totalFeatureCredits,
                  ingressCredits: isFinalTokenType
                    ? totalIngressCredits
                    : (chat.aggregateMetrics.ingressCredits || 0) + totalIngressCredits,
                  egressCredits: isFinalTokenType
                    ? totalEgressCredits
                    : (chat.aggregateMetrics.egressCredits || 0) + totalEgressCredits,
                };
              }
              linkedMessage.aggregateMetrics = {
                ...linkedMessage.aggregateMetrics,
                totalCredits: isFinalTokenType
                  ? totalFeatureCredits
                  : (linkedMessage.aggregateMetrics.totalCredits || 0) + totalFeatureCredits,
                ingressCredits: isFinalTokenType
                  ? totalIngressCredits
                  : (linkedMessage.aggregateMetrics.ingressCredits || 0) + totalIngressCredits,
                egressCredits: isFinalTokenType
                  ? totalEgressCredits
                  : (linkedMessage.aggregateMetrics.egressCredits || 0) + totalEgressCredits,
              };
            }
          }
        }
      }

      // If finalContent wasn't set from message data, try to get it from
      // aggregated delta content (for tools like report, chart, image, presentation
      // that emit JSON via deltas rather than structured message data)
      if (!block.finalContent) {
        const aggregatedContent = getBlockContent(blockId);
        if (aggregatedContent) {
          block.finalContent = { type: "text", text: aggregatedContent };
        }
      }
    })
  );

  // Clean up granular store data for this block (content chunks, delta subscribers)
  emitBlockStop(blockId);
  dispatchGlobalEvent("block-completed", { blockId });
};

const processTaskStart = (setStore: SetStreamingStore, message: ChatMessages.Message, eventTimestamp?: Date) => {
  if (message.data.case !== "messageTaskData") {
    console.error(new Error(`Expected messageTaskData, got ${message.data.case}`));
    return;
  }

  const {
    id,
    operationId: _operationId,
    data: { value: data },
  } = message;
  const operationId = _operationId as StreamingOperationId;
  const messageId = id as StreamingMessageId;

  const streamingTask: StreamingTask = {
    taskName: data.taskName,
    currentActivity: data.currentActivity,
  };

  setStore(
    produce((store) => {
      // Special case for end_turn_v1, it doesn't have an operation
      // so we have to create a mock one
      if (!store.operations[operationId]) {
        const operation: StreamingOperation = {
          index: store.messages[messageId]?.operations.length ?? 0,
          isComplete: true,
          operationId,
          messageId,
          blocks: [],
          name: "Answer Complete",
          activity: "conversation_complete",
          startedAt: undefined,
          completedAt: eventTimestamp || new Date(),
          role: ChatMessages.Role.UNSPECIFIED,
          status: ChatMessages.OperationStatus.COMPLETED,
          task: streamingTask,
          progressMessage: undefined,
          parentOperationId: undefined,
        };
        store.operations[operationId] = operation;
        attachOperationToMessage(store, messageId, operationId);
      } else {
        store.operations[operationId].task = streamingTask;
      }
    })
  );
};

const mutatingUpdateExistingTask = (task: StreamingTask, data: ChatMessages.MessageTaskData) => {
  if (data.currentActivity !== task.currentActivity) {
    task.currentActivity = data.currentActivity;
  }
  if (data.taskName !== task.taskName) {
    task.taskName = data.taskName;
  }
};
const processTaskDelta = (setStore: SetStreamingStore, message: ChatMessages.Message, eventTimestamp?: Date) => {
  if (message.data.case !== "messageTaskData") {
    console.error(new Error(`Expected messageTaskData, got ${message.data.case}`));
    return;
  }

  const {
    operationId: _operationId,
    id: _messageId,
    data: { value: data },
  } = message;
  const operationId = _operationId as StreamingOperationId;
  const messageId = _messageId as StreamingMessageId;

  const streamingTask: StreamingTask = {
    taskName: data.taskName,
    currentActivity: data.currentActivity,
  };

  setStore(
    produce((store) => {
      if (!store.operations[operationId]) {
        const operation: StreamingOperation = {
          index: store.messages[messageId]?.operations.length ?? 0,
          isComplete: true,
          operationId,
          messageId,
          blocks: [],
          name: "Answer Complete",
          activity: "conversation_complete",
          startedAt: undefined,
          completedAt: eventTimestamp || new Date(),
          role: ChatMessages.Role.UNSPECIFIED,
          status: ChatMessages.OperationStatus.COMPLETED,
          task: streamingTask,
          progressMessage: undefined,
          parentOperationId: undefined,
        };
        store.operations[operationId] = operation;
      }

      attachOperationToMessage(store, messageId, operationId);

      if (!store.operations[operationId]?.task) {
        store.operations[operationId].task = streamingTask;
      } else {
        mutatingUpdateExistingTask(store.operations[operationId].task, data);
      }
    })
  );
};
